import { SCENE_EVENTS } from "../MVC/EVENTS/ModelEvents";
import { generateIconElement, ICON } from "../Icons/Icons";
import { createPopper } from '@popperjs/core';

/**
 * NavMenuGroup - Represents a logical grouping of menu items
 *
 * Groups are used to organize related menu items together. Each group can optionally
 * have a title and is visually separated from other groups by a divider line.
 *
 * @example
 * // Group with title
 * new NavMenuGroup({
 *   name: 'profile-settings',
 *   title: 'Profile Settings',
 *   titleLangKey: 'profile-settings-title',
 *   items: [
 *     new NavMenuItem({ label: 'Edit Profile', url: '/profile/edit' }),
 *     new NavMenuItem({ label: 'Change Password', url: '/profile/password' })
 *   ]
 * });
 *
 * @example
 * // Group without title (just items with divider)
 * new NavMenuGroup({
 *   name: 'actions',
 *   items: [
 *     new NavMenuItem({ label: 'Logout', onClick: () => logout() })
 *   ]
 * });
 */
class NavMenuGroup {
  /**
   * Create a menu group
   *
   * @param {Object} config - Group configuration
   * @param {jQuery|string|null} [config.template=null] - Custom jQuery template for the entire group (overrides default rendering)
   * @param {string|null} [config.name=null] - Unique identifier for the group (used for data attributes)
   * @param {string|null} [config.title=null] - Display title for the group (shown above items)
   * @param {string|null} [config.titleLangKey=null] - Translation key for the title (for i18n support)
   * @param {NavMenuItem[]} [config.items=[]] - Array of menu items in this group
   */
  constructor({
      template = null,
      name = null,
      title = null,
      titleLangKey = null,
      items = [],
    }) {
    this.template = template ? $(template) : null;
    this.name = name;
    this.titleLangKey = titleLangKey;
    this.title = title || (titleLangKey ? $trans(titleLangKey) : null);
    this.items = items;
  }

  /**
   * Render the group to a jQuery element
   *
   * Creates a <ul> element containing:
   * - Optional title (if provided)
   * - All menu items in the group
   * - Bottom divider line
   *
   * @returns {jQuery} The rendered group element
   */
  render() {
    let $template = '';
    if (this.template) {
      $template = this.template;
    } else {
      $template = $(`<ul class="nav-menu__group"></ul>`)

      if (this.title) {
        $template.append(`
<li class="nav-menu__group-title" data-group-name="${this.name}"><p data-lang='${this.titleLangKey}'>${this.title}</p></li>
        `);
      }

      this.items.forEach((item) => {
        /** @var NavMenuItem $item */
        const $item = item.render({ groupName: this.name });

        $template.append($item);
      })

      $template.append(`<hr class="nav-menu__group-bottom-divider">`);
    }

    $template.data('controller', this);

    return $template;
  }
}

/**
 * NavMenuItem - Represents a single clickable item in a menu
 *
 * Menu items are the individual rows in a menu. Each item can:
 * - Be a simple link (with url)
 * - Execute a function on click (with onClick)
 * - Trigger a submenu (with submenu)
 * - Display an icon and label
 *
 * @example
 * // Simple link item
 * new NavMenuItem({
 *   label: 'My Profile',
 *   url: '/profile',
 *   icon: ICON.USER
 * });
 *
 * @example
 * // Item with click handler
 * new NavMenuItem({
 *   label: 'Logout',
 *   id: 'logout-btn',
 *   onClick: () => {
 *     $('#logout-form').submit();
 *   }
 * });
 *
 * @example
 * // Item that triggers a submenu
 * new NavMenuItem({
 *   label: 'Settings',
 *   submenu: new NavMenu({
 *     id: 'settings-submenu',
 *     items: [
 *       new NavMenuItem({ label: 'General', url: '/settings/general' }),
 *       new NavMenuItem({ label: 'Privacy', url: '/settings/privacy' })
 *     ]
 *   })
 * });
 */
class NavMenuItem {
  /** @type {NavMenu|null} Submenu instance if this item triggers a submenu */
  submenu = null;

  /**
   * Create a menu item
   *
   * @param {Object} config - Item configuration
   * @param {jQuery|string|null} [config.template=null] - Custom jQuery template (overrides default rendering)
   * @param {string|null} [config.id=null] - HTML id attribute for the item
   * @param {string|string[]|null} [config.classes=null] - CSS classes to add to the item
   * @param {jQuery|string|null} [config.icon=null] - Icon element to display before the label
   * @param {string|null} [config.label=null] - Display text for the item
   * @param {string|null} [config.labelLangKey=null] - Translation key for the label (for i18n support)
   * @param {string|null} [config.url=null] - URL to navigate to when clicked (creates <a> tag)
   * @param {Function|null} [config.onClick=null] - Click handler function (only used if no submenu)
   * @param {NavMenu|null} [config.submenu=null] - Submenu to show when this item is hovered/clicked
   * @param {Object} [config.attributes={}] - Additional HTML attributes to add to the item
   */
  constructor({
      template = null,
      id = null,
      classes = null,
      icon = null,
      label = null,
      labelLangKey = null,
      url = null,
      onClick = null,
      submenu = null,
      attributes = {}
    }) {
    this.template = template;

    // if it has template, it is a custom item
    if (!this.template) {
      this.id = id;
      this.classes = classes;
      this.attributes = attributes;
      this.icon = icon;
      this.labelLangKey = labelLangKey;
      if (this.labelLangKey) {
        this.label = $trans(this.labelLangKey)
      } else {
        this.label = label
      }
      this.url = url;
    }

    this.submenu = submenu;

    // if it has children, it is a parent item
    if (!this.submenu) {
      this.onClick = onClick;
    }
  }

  /**
   * Render the item to a jQuery element
   *
   * Creates a <li> element containing:
   * - Link (<a>) or span based on whether url is provided
   * - Optional icon
   * - Label text
   * - Arrow icon if item has a submenu
   * - Event handlers for click/hover
   *
   * @param {Object} [options={}] - Render options
   * @param {string|null} [options.groupName=null] - Name of parent group (added as data attribute)
   * @returns {jQuery} The rendered item element
   */
  render({ groupName = null } = {}) {
    let $template = '';
    if (this.template) {
      $template = $(this.template)
    } else {
      const $itemContent = $(this.url ? `<a href="${this.url}"></a>` : '<span></span>')

      if (this.id) {
        $itemContent.attr('id', this.id)
      }
      $itemContent.addClass(this.classes)
      Object.keys(this.attributes || {}).forEach((key) => {
        $itemContent.attr(key, this.attributes[key])
      })

      if (this.icon) {
        $itemContent.append(this.icon);
      }
      $itemContent.append(`<span data-lang="${this.labelLangKey}">${this.label}</span>`)

      $template = $(`<li class="nav-menu__group-item clickable"></li>`)

      $template.append($itemContent)
    }

    if (groupName) {
      $template.attr('data-group-name', groupName)
    }

    if (this.submenu) {
      // Item with submenu - only toggle submenu, don't close main menu
      $template.append(
        generateIconElement(ICON.ARROW_RIGHT, {
          classes: ['nav-menu__group-item-arrow-icon'],
        })
      )
      this.submenu.registerAsSubmenu({
        triggerEl: $template,
        position: 'right',
        positionOffset: [0, 4],
        onToggle: (isOpen) => {
          $template.toggleClass('nav-menu__group-item--is-active', isOpen)
        }
      });
    } else if (this.onClick) {
      const onClick = this.onClick
      // Item without submenu - close all menus on click
      $template.on('click', (e) => {
        onClick(e);
        // Close all main menus
        mainMenuList.forEach(menu => menu.hide());
      });
    } else {
      // Regular link item - close all menus on click
      $template.on('click', () => {
        mainMenuList.forEach(menu => menu.hide());
      });
    }

    $template.data('controller', this)

    return $template;
  }
}

/**
 * Global registry of all main menus (top-level menus)
 * Used for managing menu visibility - when one main menu opens, others close
 * @type {NavMenu[]}
 */
let mainMenuList = [];

/**
 * Global registry of all submenus
 * Used for managing submenu visibility - when one submenu opens, others close
 * @type {NavMenu[]}
 */
let subMenuList = [];

/**
 * NavMenu - A standalone menu component with positioning and interaction support
 *
 * NavMenu is the main menu container that can be used as:
 * - A top-level menu (registered with registerAsMainMenu)
 * - A submenu triggered by a menu item (registered with registerAsSubmenu)
 *
 * Features:
 * - Automatic positioning using Popper.js
 * - Click-outside-to-close for main menus
 * - Hover support for submenus
 * - Proper cleanup to prevent memory leaks
 *
 * @example
 * // Create a main menu
 * const userMenu = new NavMenu({
 *   id: 'user-menu',
 *   classes: ['user-menu'],
 *   items: [
 *     new NavMenuGroup({
 *       name: 'profile',
 *       items: [
 *         new NavMenuItem({ label: 'My Profile', url: '/profile' })
 *       ]
 *     })
 *   ]
 * });
 *
 * userMenu.registerAsMainMenu({
 *   triggerEl: $('#user-avatar'),
 *   position: 'bottom-end'
 * });
 *
 * @example
 * // Create a submenu (usually done inside NavMenuItem)
 * const settingsSubmenu = new NavMenu({
 *   id: 'settings-submenu',
 *   items: [
 *     new NavMenuItem({ label: 'General', url: '/settings/general' }),
 *     new NavMenuItem({ label: 'Privacy', url: '/settings/privacy' })
 *   ]
 * });
 *
 * settingsSubmenu.registerAsSubmenu({
 *   triggerEl: $settingsItem,
 *   position: 'right'
 * });
 */
class NavMenu {
  /**
   * Create a menu
   *
   * @param {Object} config - Menu configuration
   * @param {string} config.id - Unique identifier for the menu (used for HTML id and event namespacing)
   * @param {string|string[]} [config.classes] - CSS classes to add to the menu container
   * @param {(NavMenuGroup|NavMenuItem)[]} config.items - Array of groups or items to display in the menu
   * @param {Object} [config.attributes] - Additional HTML attributes for the menu container
   */
  constructor({
    id,
    classes,
    items,
    attributes,
  }) {
    this.isSubmenu = null;

    this.id = id;
    this.classes = classes;
    this.attributes = attributes;
    this.items = items;

    this.$template = $(this.render())
    this.popperInstance = null;
    this.hoverTimeout = null;
  }

  /**
   * Render the menu to a jQuery element
   *
   * Creates a <nav> element with class 'nav-menu' containing:
   * - All groups/items from the items array
   * - Applied id, classes, and attributes
   *
   * @returns {jQuery} The rendered menu element
   */
  render() {
    const $template = $(`
<nav class="nav-menu">
  <ul></ul>
</nav>
    `)

    this.items.forEach((item) => {
      $template.find('> ul').append(item.render())
    })

    $template.attr('id', this.id)
    $template.addClass(this.classes)
    Object.keys(this.attributes || {}).forEach((key) => {
      $template.attr(key, this.attributes[key])
    })

    $template.data('controller', this)

    return $template;
  }

  /**
   * Register this menu as a submenu
   *
   * Submenus:
   * - Are triggered by hovering over a menu item
   * - Position themselves relative to the trigger element
   * - Close when mouse leaves both trigger and submenu
   * - Are added to the global subMenuList
   *
   * @param {Object} options - Registration options
   * @param {jQuery|HTMLElement} options.triggerEl - Element that triggers this submenu (usually a menu item)
   * @param {string} options.position - Popper.js placement (e.g., 'right', 'left', 'bottom')
   * @param {Function} [options.onToggle] - Callback when submenu opens/closes, receives (isOpen: boolean)
   * @returns {NavMenu} This menu instance for chaining
   */
  registerAsSubmenu({ triggerEl, position, onToggle }) {
    this.isSubmenu = true;
    subMenuList.push(this);
    this.register({ triggerEl, position, onToggle });
    return this;
  }

  /**
   * Register this menu as a main menu
   *
   * Main menus:
   * - Are triggered by clicking a trigger element
   * - Close when clicking outside the menu
   * - Close other main menus when opened
   * - Are added to the global mainMenuList
   *
   * @param {Object} options - Registration options
   * @param {jQuery|HTMLElement} options.triggerEl - Element that triggers this menu (e.g., user avatar, menu button)
   * @param {string} options.position - Popper.js placement (e.g., 'bottom-end', 'bottom-start', 'top')
   * @param {Function} [options.onToggle] - Callback when menu opens/closes, receives (isOpen: boolean)
   * @returns {NavMenu} This menu instance for chaining
   */
  registerAsMainMenu({ triggerEl, position, onToggle }) {
    this.isSubmenu = false;
    mainMenuList.push(this);
    this.register({ triggerEl, position, onToggle });
    return this;
  }

  /**
   * Internal method to register the menu (used by registerAsMainMenu and registerAsSubmenu)
   *
   * Sets up:
   * - Popper.js positioning
   * - Click handlers
   * - Hover handlers (for submenus)
   * - Click-outside handlers (for main menus)
   *
   * @private
   * @param {Object} options - Registration options
   * @param {jQuery|HTMLElement} options.triggerEl - Element that triggers this menu
   * @param {string} options.position - Popper.js placement
   * @param {Function} [options.onToggle] - Callback when menu opens/closes
   * @returns {NavMenu} This menu instance for chaining
   */
  register({ triggerEl, position, positionOffset = [0, 8], onToggle }) {
    this.position = position;
    this.onToggle = onToggle;
    this.triggerEl = triggerEl;

    $(document.body).append(this.$template)

    // Get DOM elements
    const triggerElement = triggerEl instanceof jQuery ? triggerEl[0] : triggerEl;
    const menuElement = this.$template[0];

    // Validate elements exist
    if (!triggerElement) {
      console.error('NavMenu: triggerEl is null or undefined');
      return this;
    }

    if (!menuElement) {
      console.error('NavMenu: menu template element is null or undefined');
      return this;
    }

    // Create Popper instance
    this.popperInstance = createPopper(triggerElement, menuElement, {
      placement: this.position,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: positionOffset,
          },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 8,
          },
        },
      ],
    });

    // Click handler
    $(triggerEl).on('click', (e) => {
      e.preventDefault();
      this.toggle()
    })

    // Setup hover handlers for submenu
    if (this.isSubmenu) {
      this.setupHoverHandlers();
    }

    // Setup click outside handler for main menu
    if (!this.isSubmenu) {
      this.setupClickOutsideHandler();
    }

    return this;
  }

  isShowing() {
    return this.$template.hasClass('is-active')
  }

  /**
   * Show the menu
   *
   * - Hides other menus in the same category (main or submenu)
   * - Adds 'is-active' class
   * - Updates Popper positioning
   * - Calls onToggle callback with true
   */
  show() {
    if (this.isSubmenu) {
      subMenuList.forEach(menu => menu.hide())
    } else {
      mainMenuList.forEach(menu => menu.hide())
    }

    this.$template.addClass('is-active')

    // Update Popper position
    if (this.popperInstance) {
      this.popperInstance.update();
    }

    this.onToggle?.(true)
  }

  /**
   * Hide the menu
   *
   * - Removes 'is-active' class
   * - Hides all submenus if this is a main menu
   * - Calls onToggle callback with false
   */
  hide() {
    this.$template.removeClass('is-active')

    // Hide all submenus
    if (!this.isSubmenu) {
      subMenuList.forEach(menu => menu.hide())
    }

    this.onToggle?.(false)
  }

  /**
   * Toggle menu visibility
   *
   * Shows the menu if hidden, hides if shown
   */
  toggle() {
    if (this.isShowing()) {
      this.hide()
    } else {
      this.show()
    }
  }

  /**
   * Setup click-outside-to-close handler for main menus
   *
   * Listens for clicks on the document and closes the menu if:
   * - Click is outside the menu element
   * - Click is not on the trigger element
   *
   * Uses a namespaced event to avoid conflicts and allow proper cleanup
   *
   * @private
   */
  setupClickOutsideHandler() {
    // Use a unique event namespace for this menu instance
    const eventNamespace = `click.navmenu-${this.id}`;

    $(document).off(eventNamespace).on(eventNamespace, (e) => {
      if (!this.$template.hasClass('is-active')) {
        return;
      }

      const $target = $(e.target);
      const triggerEl = this.triggerEl instanceof jQuery ? this.triggerEl[0] : this.triggerEl;

      // Check if click is outside menu and trigger
      const isClickInsideMenu = $target.closest(this.$template).length > 0;
      const isClickOnTrigger = $target.closest(triggerEl).length > 0;

      if (!isClickInsideMenu && !isClickOnTrigger) {
        this.hide();
      }
    });
  }

  /**
   * Setup hover handlers for submenus
   *
   * Behavior:
   * - Hovering over trigger element shows the submenu
   * - Hovering over submenu keeps it open
   * - Moving mouse away from both trigger and submenu hides it after a delay
   *
   * The delay (150ms) prevents flickering when moving mouse between trigger and submenu
   *
   * Note: Hover is disabled on touch devices to prevent conflicts with click events
   *
   * @private
   */
  setupHoverHandlers() {
    // Check if device supports hover (not a touch device)
    // Touch devices will use click only to avoid double-tap issues
    const isTouchDevice = ('ontouchstart' in window) ||
                          (navigator.maxTouchPoints > 0) ||
                          (navigator.msMaxTouchPoints > 0);

    if (isTouchDevice) {
      // On touch devices, only use click - no hover
      return;
    }

    const HOVER_DELAY = 150; // ms delay before hiding

    const clearHoverTimeout = () => {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
    };

    const scheduleHide = () => {
      clearHoverTimeout();
      this.hoverTimeout = setTimeout(() => {
        this.hide();
      }, HOVER_DELAY);
    };

    // Hover on trigger element
    $(this.triggerEl).on('mouseenter', () => {
      clearHoverTimeout();
      this.show();
    });

    $(this.triggerEl).on('mouseleave', () => {
      scheduleHide();
    });

    // Hover on submenu
    this.$template.on('mouseenter', () => {
      clearHoverTimeout();
    });

    this.$template.on('mouseleave', () => {
      scheduleHide();
    });
  }

  /**
   * Destroy the menu and clean up resources
   *
   * Cleanup includes:
   * - Clearing hover timeouts
   * - Destroying Popper instance
   * - Removing event listeners
   * - Removing from global menu lists
   * - Removing DOM element
   *
   * Call this when the menu is no longer needed to prevent memory leaks
   */
  destroy() {
    // Clear hover timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    // Destroy Popper instance
    if (this.popperInstance) {
      this.popperInstance.destroy();
      this.popperInstance = null;
    }

    // Remove click outside handler
    if (!this.isSubmenu) {
      $(document).off(`click.navmenu-${this.id}`);
    }

    // Remove event listeners
    if (this.triggerEl) {
      $(this.triggerEl).off('click mouseenter mouseleave');
    }
    this.$template.off('mouseenter mouseleave');

    // Remove from lists
    if (this.isSubmenu) {
      const index = subMenuList.indexOf(this);
      if (index > -1) subMenuList.splice(index, 1);
    } else {
      const index = mainMenuList.indexOf(this);
      if (index > -1) mainMenuList.splice(index, 1);
    }

    // Remove from DOM
    this.$template.remove();
  }
}

export {
  NavMenuGroup,
  NavMenuItem,
  NavMenu,
}

